import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { Modal, Form, Input, Button, Spin, message } from 'antd';
import DocumentService from '../../../services/DocumentService';
import { PDFDocument, PDFFont, StandardFonts, rgb, degrees } from 'pdf-lib';
import { turkishToEnglish } from '../../../utils';

const PdfViewer = lazy(() => import('./PdfViewer'));

interface ShareDocumentModalProps {
  document: {
    title?: string;
    url: string;
  };
}

interface WatermarkPlacement {
  x: number;
  y: number;
  size: number;
  lineHeight: number;
}

interface WatermarkBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

const WATERMARK_ANGLE = 40;
const WATERMARK_COLUMNS = 2;
const WATERMARK_ROWS = 4;
const WATERMARK_MAX_FONT_SIZE = 25;
const WATERMARK_LINE_HEIGHT_RATIO = 1.2;

const getRotatedWatermarkBounds = (
  text: string,
  font: PDFFont,
  placement: WatermarkPlacement,
): WatermarkBounds => {
  const lines = text.split('\n');
  const textWidth = Math.max(
    ...lines.map((line) => font.widthOfTextAtSize(line, placement.size)),
    0.01,
  );
  const ascenderHeight = font.heightAtSize(placement.size, { descender: false });
  const fontHeight = font.heightAtSize(placement.size);
  const descenderHeight = Math.max(fontHeight - ascenderHeight, 0);
  const localMinY = -((lines.length - 1) * placement.lineHeight) - descenderHeight;
  const localMaxY = ascenderHeight;
  const radians = WATERMARK_ANGLE * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const corners = [
    { x: 0, y: localMinY },
    { x: textWidth, y: localMinY },
    { x: 0, y: localMaxY },
    { x: textWidth, y: localMaxY },
  ].map(({ x, y }) => ({
    x: placement.x + x * cos - y * sin,
    y: placement.y + x * sin + y * cos,
  }));

  return {
    minX: Math.min(...corners.map(({ x }) => x)),
    maxX: Math.max(...corners.map(({ x }) => x)),
    minY: Math.min(...corners.map(({ y }) => y)),
    maxY: Math.max(...corners.map(({ y }) => y)),
  };
};

export const createWatermarkPlacements = (
  pageWidth: number,
  pageHeight: number,
  text: string,
  font: PDFFont,
): WatermarkPlacement[] => {
  const pageMargin = Math.min(12, pageWidth * 0.04, pageHeight * 0.04);
  const cellGap = Math.min(8, pageWidth * 0.02, pageHeight * 0.02);
  const cellWidth = (
    pageWidth - pageMargin * 2 - cellGap * (WATERMARK_COLUMNS - 1)
  ) / WATERMARK_COLUMNS;
  const cellHeight = (
    pageHeight - pageMargin * 2 - cellGap * (WATERMARK_ROWS - 1)
  ) / WATERMARK_ROWS;
  const cellPadding = Math.min(4, cellWidth * 0.05, cellHeight * 0.05);
  const unitPlacement = {
    x: 0,
    y: 0,
    size: 1,
    lineHeight: WATERMARK_LINE_HEIGHT_RATIO,
  };
  const unitBounds = getRotatedWatermarkBounds(text, font, unitPlacement);
  const unitWidth = unitBounds.maxX - unitBounds.minX;
  const unitHeight = unitBounds.maxY - unitBounds.minY;
  const fontSize = Math.min(
    WATERMARK_MAX_FONT_SIZE,
    Math.max((cellWidth - cellPadding * 2) / unitWidth, Number.EPSILON),
    Math.max((cellHeight - cellPadding * 2) / unitHeight, Number.EPSILON),
  );
  const lineHeight = fontSize * WATERMARK_LINE_HEIGHT_RATIO;
  const scaledBounds = getRotatedWatermarkBounds(text, font, {
    x: 0,
    y: 0,
    size: fontSize,
    lineHeight,
  });
  const watermarkWidth = scaledBounds.maxX - scaledBounds.minX;
  const watermarkHeight = scaledBounds.maxY - scaledBounds.minY;
  const placements: WatermarkPlacement[] = [];

  for (let row = 0; row < WATERMARK_ROWS; row += 1) {
    for (let column = 0; column < WATERMARK_COLUMNS; column += 1) {
      const cellLeft = pageMargin + column * (cellWidth + cellGap);
      const cellBottom = pageMargin + row * (cellHeight + cellGap);

      placements.push({
        x: cellLeft + (cellWidth - watermarkWidth) / 2 - scaledBounds.minX,
        y: cellBottom + (cellHeight - watermarkHeight) / 2 - scaledBounds.minY,
        size: fontSize,
        lineHeight,
      });
    }
  }

  return placements;
};

export const getWatermarkBounds = getRotatedWatermarkBounds;

const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({ document }) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [pdfFile, setPdfFile] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const loadingRef = useRef(false);

  useEffect(() => () => {
    requestIdRef.current += 1;
    loadingRef.current = false;
  }, []);

  const handleCreate = ({ name, email }: { name: string; email: string }) => {
    if (loadingRef.current) {
      return;
    }

    const requestId = ++requestIdRef.current;
    loadingRef.current = true;
    setLoading(true);
    setPdfFile(null);

    DocumentService.getFile(document.url)
      .then(async (response) => {
        try {
          const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
          const arrayBuffer = await pdfBlob.arrayBuffer();

          const pdfDoc = await PDFDocument.load(arrayBuffer);
          const watermarkFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

          const pages = pdfDoc.getPages();
          pages.forEach(page => {
            const fullName = turkishToEnglish(name);

            const { width, height } = page.getSize();
            const text = `${fullName}\n${email}`;
            const placements = createWatermarkPlacements(
              width,
              height,
              text,
              watermarkFont,
            );

            placements.forEach(({ x, y, size, lineHeight }) => {
              page.drawText(text, {
                x,
                y,
                size,
                lineHeight,
                font: watermarkFont,
                color: rgb(0.75, 0.75, 0.75),
                rotate: degrees(WATERMARK_ANGLE),
                opacity: 0.7
              });
            });
          });

          const newPdfBytes = await pdfDoc.save();
          const newPdfBlob = new Blob([newPdfBytes], { type: 'application/pdf' });

          if (requestIdRef.current === requestId) {
            setPdfFile(newPdfBlob);
          }
        } catch (_error) {
          if (requestIdRef.current === requestId) {
            message.error('PDF işleme sırasında hata oluştu.');
          }
        } finally {
          if (requestIdRef.current === requestId) {
            loadingRef.current = false;
            setLoading(false);
          }
        }
      }).catch(() => {
        if (requestIdRef.current === requestId) {
          loadingRef.current = false;
          setLoading(false);
          message.error('PDF dosyasını alırken hata oluştu.');
        }
      });
  };

  const handleOpen = () => {
    setModalVisible(true);
  };

  const handleClose = () => {
    requestIdRef.current += 1;
    loadingRef.current = false;
    setModalVisible(false);
    setLoading(false);
    form.resetFields();
    setPdfFile(null);
  };

  const safeTitle = (document.title || 'dokuman').replace(/[\\/:*?"<>|]+/g, '-');
  const downloadFileName = `${safeTitle}-filigranli.pdf`;

  return (
    <>
      <Button className='mr-12' type="primary" onClick={handleOpen}>Paylaş</Button>
      <Modal
        title="Doküman Paylaş"
        open={isModalVisible}
        onCancel={handleClose}
        footer={null}
        width={760}
        className="share-document-modal"
        destroyOnClose
      >
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" onFinish={handleCreate}>
            <Form.Item
              name="name"
              label="Ad Soyad"
              rules={[{ required: true, message: 'Lütfen ad soyad giriniz' }]}
            >
              <Input disabled={loading} />
            </Form.Item>
            <Form.Item
              name="email"
              label="E-posta"
              rules={[
                { required: true, message: 'Lütfen e-posta adresi giriniz' },
                { type: 'email', message: 'Geçerli bir e-posta adresi giriniz' },
              ]}
            >
              <Input disabled={loading} type="email" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Oluştur
              </Button>
            </Form.Item>
          </Form>
          {pdfFile && (
            <Suspense fallback={<div className="pdf-viewer__status"><Spin /></div>}>
              <PdfViewer file={pdfFile} fileName={downloadFileName} />
            </Suspense>
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default ShareDocumentModal;
